import { useState, useRef, useCallback, useEffect, type RefObject } from 'react'

interface Transform {
    scale: number
    x: number
    y: number
}

interface UseMermaidZoomPanOptions {
    containerRef: RefObject<HTMLElement>
    canvasRef: RefObject<HTMLElement>
    svg?: string | null
    minScale?: number
    maxScale?: number
    zoomStep?: number
    doubleTapScale?: number
    onInteraction?: () => void
}

interface IntrinsicSize {
    width: number
    height: number
    minX: number
    minY: number
}

// Helper: Calculate center and distance of two pointers for pinch-to-zoom
function getPointersInfo(pointers: Map<number, { x: number; y: number }>) {
    const pts = Array.from(pointers.values())
    if (pts.length < 2) return null

    const p1 = pts[0]
    const p2 = pts[1]

    const cx = (p1.x + p2.x) / 2
    const cy = (p1.y + p2.y) / 2
    const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y)

    return { cx, cy, dist }
}

export function useMermaidZoomPan({
    containerRef,
    canvasRef,
    svg,
    minScale = 0.1,
    maxScale = 5,
    zoomStep = 0.25,
    doubleTapScale = 2,
    onInteraction,
}: UseMermaidZoomPanOptions) {
    const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 })
    const [intrinsicSize, setIntrinsicSize] = useState<IntrinsicSize | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [animated, setAnimated] = useState(false)

    // Refs for gesture tracking (avoids stale closures)
    const transformRef = useRef(transform)
    const fitScaleRef = useRef<number>(1)

    // Safety flag to prevent auto-fit from ripping the camera away from the user
    // during background SVG updates (e.g. MCP live-preview) if they have manually panned/zoomed.
    const hasUserManipulatedRef = useRef<boolean>(false)

    // Pointer tracking map for robust multi-touch support
    const activePointers = useRef<Map<number, { x: number; y: number }>>(new Map())

    // State of the gesture when it started or last updated
    const gestureState = useRef<{
        type: 'none' | 'pan' | 'pinch'
        startX: number
        startY: number
        startTx: number
        startTy: number
        startScale: number
        startDist: number
        lastCx: number
        lastCy: number
    }>({
        type: 'none',
        startX: 0, startY: 0,
        startTx: 0, startTy: 0,
        startScale: 1, startDist: 0,
        lastCx: 0, lastCy: 0
    })

    // Sync refs
    useEffect(() => { transformRef.current = transform }, [transform])

    const clampScale = useCallback((s: number) => Math.min(maxScale, Math.max(minScale, s)), [maxScale, minScale])

    // Calculates clamped x/y translations to prevent the SVG from being dragged completely out of view
    const clampTranslation = useCallback((x: number, y: number, scale: number) => {
        if (!containerRef.current || !intrinsicSize) return { x, y }

        const rect = containerRef.current.getBoundingClientRect()
        // The actual rendered pixel dimensions of the SVG
        const svgRenderedW = intrinsicSize.width * scale
        const svgRenderedH = intrinsicSize.height * scale

        // Minimum pixels that must remain visible inside the container
        const edgeMargin = 50

        // If dragged left, right edge shouldn't cross the left margin. 
        // If dragged right, left edge shouldn't cross the right margin.
        const minX = -svgRenderedW + edgeMargin
        const maxX = rect.width - edgeMargin

        const minY = -svgRenderedH + edgeMargin
        const maxY = rect.height - edgeMargin

        return {
            x: Math.min(maxX, Math.max(minX, x)),
            y: Math.min(maxY, Math.max(minY, y))
        }
    }, [containerRef, intrinsicSize])

    // ── Fit to View ──────────────────────────────────────────────

    const fitToView = useCallback((animate = true, forceReset = true) => {
        const container = containerRef.current
        const canvas = canvasRef.current
        if (!container || !canvas) return

        const svgEl = canvas.querySelector('svg')
        if (!svgEl) return

        // Robust measurement using viewBox if available
        let svgW = svgEl.clientWidth || 1000
        let svgH = svgEl.clientHeight || 500
        let minX = 0
        let minY = 0

        const viewBox = svgEl.getAttribute('viewBox')
        if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number)
            if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
                minX = parts[0]
                minY = parts[1]
                svgW = parts[2]
                svgH = parts[3]
            }
        }

        // Protect against NaN/Infinity if width/height is 0 (Element hidden or broken CSS)
        // SVG dimensions must logically be at least 1px to divide correctly.
        svgW = Math.max(1, svgW)
        svgH = Math.max(1, svgH)

        const newIntrinsic: IntrinsicSize = { width: svgW, height: svgH, minX, minY }
        setIntrinsicSize(newIntrinsic)

        const rect = container.getBoundingClientRect()
        const padding = 32
        const availW = rect.width - padding * 2
        const availH = rect.height - padding * 2

        const fitScale = Math.min(availW / svgW, availH / svgH, 1)
        // Center the diagram. Account for the fact that coordinate (minX, minY) 
        // is the top-left of the content inside the SVG element.
        const fitX = (rect.width - svgW * fitScale) / 2
        const fitY = (rect.height - svgH * fitScale) / 2

        fitScaleRef.current = fitScale

        // If the user has manipulated the camera and we are soft-fitting (e.g. background update),
        // we DO NOT reset the camera. However, we MUST mathematically clamp the current
        // coordinates to the NEW intrinsic size so the camera isn't left out-of-bounds
        // if the new diagram is drastically smaller than the old one.
        if (!forceReset && hasUserManipulatedRef.current) {
            setTransform(current => {
                // We must use the raw math here to avoid relying on stale state in `clampTranslation`
                const svgRenderedW = newIntrinsic.width * current.scale
                const svgRenderedH = newIntrinsic.height * current.scale
                const edgeMargin = 50
                const minTx = -svgRenderedW + edgeMargin
                const maxTx = rect.width - edgeMargin
                const minTy = -svgRenderedH + edgeMargin
                const maxTy = rect.height - edgeMargin

                return {
                    ...current,
                    x: Math.min(maxTx, Math.max(minTx, current.x)),
                    y: Math.min(maxTy, Math.max(minTy, current.y))
                }
            })
            return;
        }

        if (forceReset) {
            hasUserManipulatedRef.current = false // Explicit manual fit allows future auto-fits
        }

        if (animate) {
            setAnimated(true)
            requestAnimationFrame(() => {
                setTransform({ scale: fitScale, x: fitX, y: fitY })
                setTimeout(() => setAnimated(false), 300)
            })
        } else {
            setTransform({ scale: fitScale, x: fitX, y: fitY })
        }
    }, [containerRef, canvasRef])

    // ── Resize Observer (Auto-Recenter on window/panel changes) ──
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let lastW = container.clientWidth
        let lastH = container.clientHeight

        const observer = new ResizeObserver(() => {
            const newW = container.clientWidth
            const newH = container.clientHeight

            // Safari bug prevention: ResizeObserver can fire repeatedly if internal
            // transforms (like zoom padding) slightly alter scrollbars or layout bounds.
            // We strictly only trigger a re-fit if the physical boundary changed by > 5px.
            if (Math.abs(newW - lastW) > 5 || Math.abs(newH - lastH) > 5) {
                lastW = newW
                lastH = newH

                // Only trigger if we aren't currently manually panning/zooming
                // ResizeObserver passes forceReset=false to avoid interrupting Live-Previews
                if (!isDragging) {
                    fitToView(false, false)
                }
            }
        })
        observer.observe(container)
        return () => observer.disconnect()
    }, [containerRef, fitToView, isDragging])

    // ── Zoom Helpers ─────────────────────────────────────────────

    const zoomAt = useCallback((cx: number, cy: number, newScale: number, animate = false) => {
        setTransform(prev => {
            const clampedScale = clampScale(newScale)
            // Prevent focal drift by short-circuiting math if scale is already capped
            if (clampedScale === prev.scale) return prev

            const ratio = clampedScale / prev.scale
            const nx = cx - (cx - prev.x) * ratio
            const ny = cy - (cy - prev.y) * ratio

            const clampedTx = clampTranslation(nx, ny, clampedScale)
            return { scale: clampedScale, x: clampedTx.x, y: clampedTx.y }
        })

        hasUserManipulatedRef.current = true
        if (animate) {
            setAnimated(true)
            setTimeout(() => setAnimated(false), 300)
        }
        onInteraction?.()
    }, [clampScale, clampTranslation, onInteraction])

    const zoomIn = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        zoomAt(rect.width / 2, rect.height / 2, transformRef.current.scale * (1 + zoomStep), true)
    }, [containerRef, zoomAt, zoomStep])

    const zoomOut = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        zoomAt(rect.width / 2, rect.height / 2, transformRef.current.scale * (1 - zoomStep), true)
    }, [containerRef, zoomAt, zoomStep])

    const resetZoom = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        zoomAt(rect.width / 2, rect.height / 2, 1, true)
    }, [containerRef, zoomAt])

    // ── Pointer Handlers (Unified Touch & Mouse) ─────────────────

    // Anchor reset logic handles N-finger adding/removing silently without jumping.
    const resetGestureAnchor = () => {
        const ptrs = activePointers.current.size
        if (ptrs === 1) {
            const p = Array.from(activePointers.current.values())[0]
            gestureState.current = {
                type: 'pan',
                startX: p.x, startY: p.y,
                startTx: transformRef.current.x, startTy: transformRef.current.y,
                startScale: transformRef.current.scale,
                startDist: 0,
                lastCx: p.x, lastCy: p.y
            }
        } else if (ptrs >= 2) {
            // If >= 2 we map everything to the center of the first two pointers.
            const info = getPointersInfo(activePointers.current)
            if (info) {
                gestureState.current = {
                    type: 'pinch',
                    startX: info.cx, startY: info.cy,
                    startTx: transformRef.current.x, startTy: transformRef.current.y,
                    startScale: transformRef.current.scale,
                    startDist: info.dist,
                    lastCx: info.cx, lastCy: info.cy
                }
            }
        }
    }

    const handlePointerDown = (e: React.PointerEvent) => {
        const target = e.target as HTMLElement
        if (target.closest('[data-no-drag]')) return
        if (e.button !== 0 && e.pointerType === 'mouse') return // Only left click

        e.stopPropagation()
        try {
            e.currentTarget.setPointerCapture(e.pointerId)
        } catch (err) {
            // iOS Safari error prevention
        }

        activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        setIsDragging(true)
        hasUserManipulatedRef.current = true
        resetGestureAnchor()
        onInteraction?.()
    }

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!activePointers.current.has(e.pointerId)) return
        e.stopPropagation()

        activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        const state = gestureState.current
        const rect = containerRef.current?.getBoundingClientRect()

        if (state.type === 'pan' && activePointers.current.size === 1) {
            const dx = e.clientX - state.startX
            const dy = e.clientY - state.startY
            setTransform(prev => {
                const nx = state.startTx + dx
                const ny = state.startTy + dy
                const clamped = clampTranslation(nx, ny, prev.scale)
                return { ...prev, x: clamped.x, y: clamped.y }
            })
            onInteraction?.()
        }
        else if (state.type === 'pinch' && activePointers.current.size === 2 && rect) {
            const info = getPointersInfo(activePointers.current)
            if (!info || state.startDist === 0) return

            // 1. Calculate new scale based on pinch spread
            const scaleFactor = info.dist / state.startDist
            const newScale = clampScale(state.startScale * scaleFactor)

            // 2. We center the zoom on the geometric middle of the fingers
            const cx = state.startX - rect.left
            const cy = state.startY - rect.top

            const ratio = newScale / state.startScale
            const zoomX = cx - (cx - state.startTx) * ratio
            const zoomY = cy - (cy - state.startTy) * ratio

            // 3. Add panning if the fingers moved together
            const panDx = info.cx - state.startX
            const panDy = info.cy - state.startY

            const clampedTx = clampTranslation(zoomX + panDx, zoomY + panDy, newScale)

            setTransform({
                scale: newScale,
                x: clampedTx.x,
                y: clampedTx.y
            })

            state.lastCx = info.cx
            state.lastCy = info.cy
            onInteraction?.()
        }
    }

    useEffect(() => {
        const handleCancel = () => {
            if (activePointers.current.size > 0) {
                activePointers.current.clear()
                setIsDragging(false)
                gestureState.current.type = 'none'
            }
        }

        window.addEventListener('blur', handleCancel)
        document.addEventListener('visibilitychange', handleCancel)
        window.addEventListener('contextmenu', handleCancel)

        return () => {
            window.removeEventListener('blur', handleCancel)
            document.removeEventListener('visibilitychange', handleCancel)
            window.removeEventListener('contextmenu', handleCancel)
        }
    }, [])

    const handlePointerUp = (e: React.PointerEvent) => {
        e.stopPropagation()
        try {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId)
            }
        } catch (err) { } // Ignore Safari quirks

        activePointers.current.delete(e.pointerId)

        if (activePointers.current.size === 0) {
            setIsDragging(false)
            gestureState.current.type = 'none'
        } else {
            // Recalculate anchor point around remaining fingers to prevent teleporting
            resetGestureAnchor()
        }
    }

    const handlePointerCancel = handlePointerUp

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        const cx = e.clientX - rect.left
        const cy = e.clientY - rect.top

        hasUserManipulatedRef.current = true

        // Add small epsilon to handle float roundings
        const isZoomed = transformRef.current.scale > (fitScaleRef.current + 0.05)
        if (isZoomed) {
            fitToView(true)
        } else {
            const doubleScale = clampScale(transformRef.current.scale * doubleTapScale)
            zoomAt(cx, cy, doubleScale, true)
        }
        onInteraction?.()
    }

    // ── Native wheel listener (Trackpad / Mouse) ─────────────────
    useEffect(() => {
        const el = containerRef.current
        if (!el || !svg) return

        const handler = (e: WheelEvent) => {
            // Collision Protection: If the user is physically touching/dragging the surface with pointers,
            // completely discard wheel events to avoid mathematical state collisions (vibrating matrix).
            if (activePointers.current.size > 0) return;

            e.preventDefault()
            e.stopPropagation()

            const rect = el.getBoundingClientRect()
            const cx = e.clientX - rect.left
            const cy = e.clientY - rect.top

            // Normalize wheel deltas for cross-browser consistency
            // Firefox often uses deltaMode 1 (lines), requiring conversion to pixels.
            let dy = e.deltaY;
            if (e.deltaMode === 1) dy *= 20; // Convert lines to ~pixels
            else if (e.deltaMode === 2) dy *= window.innerHeight; // Convert pages to pixels

            let dx = e.deltaX;
            if (e.deltaMode === 1) dx *= 20;
            else if (e.deltaMode === 2) dx *= window.innerWidth;

            // Trackpads & Browsers send e.ctrlKey=true for pinch-to-zoom gestures
            hasUserManipulatedRef.current = true

            if (e.ctrlKey) {
                // Smooth exponential scaling (Figma/Miro standard). 
                const zoomFactor = Math.exp(-dy / 400)

                // Functional update guarantees no dropped deltas on high-refresh (120Hz) trackpads
                setTransform(current => {
                    const newScale = clampScale(current.scale * zoomFactor)

                    // If we hit scale limits, prevent drifting the focal point
                    if (newScale === current.scale) return current;

                    const ratio = newScale / current.scale
                    const nx = cx - (cx - current.x) * ratio
                    const ny = cy - (cy - current.y) * ratio

                    const clampedTx = clampTranslation(nx, ny, newScale)
                    return { scale: newScale, x: clampedTx.x, y: clampedTx.y }
                })
            } else {
                // 2D Panning (Shift+Scroll natively sets deltaX, standard scroll sets deltaY)
                setTransform(current => {
                    const nx = current.x - dx
                    const ny = current.y - dy
                    const clampedTx = clampTranslation(nx, ny, current.scale)
                    return { scale: current.scale, x: clampedTx.x, y: clampedTx.y }
                })
            }
            onInteraction?.()
        }

        // passive: false is strict requirement to block native body scroll and browser-level pinch zoom
        el.addEventListener('wheel', handler, { passive: false })
        return () => el.removeEventListener('wheel', handler)
    }, [svg, containerRef, clampScale, clampTranslation, onInteraction])

    // Wait for svg layout
    useEffect(() => {
        // Run a soft-fit on SVG updates (forceReset = false). This parses intrinsic bounds
        // without destroying the user's camera if they are in the middle of active editing
        if (svg) {
            fitToView(false, false)
        }
    }, [svg, fitToView])

    return {
        transform,
        setTransform,
        animated,
        setAnimated,
        isDragging,
        handlers: {
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerCancel,
            onDoubleClick: handleDoubleClick,
            // Disable native drag explicitly
            onDragStart: (e: React.DragEvent) => e.preventDefault()
        },
        controls: {
            fitToView,
            zoomIn,
            zoomOut,
            zoomAt,
            resetZoom,
        },
        fitScaleRef,
        intrinsicSize,
    }
}

